import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useRequireAuth } from '@/hooks/use-require-auth';
import { listEvents } from '@/lib/events-api';
import { listMySubmissions, withdrawSubmission, fetchSubmissionThumbnailDataUrl } from '@/lib/submissions-api';
import { ArrowLeft, Lock, Image, Film, CheckCircle, Upload, XCircle, Plus } from 'lucide-react';

export default function MyViewsPage() {
  const navigate = useNavigate();
  const { ready } = useRequireAuth();
  const [sp] = useSearchParams();
  const passedEventId = sp.get('event_id') || '';
  const [eventId, setEventId] = useState(passedEventId);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [thumbs, setThumbs] = useState({});

  // Auto-resolve event if not passed
  useEffect(() => {
    if (eventId || !ready) return;
    (async () => {
      try {
        const body = await listEvents();
        const att = body.attending || [];
        // Prefer event where user can upload, or has most activity
        const sorted = [...att].sort((a, b) => {
          const aUp = a.membership?.canUpload ? 1 : 0;
          const bUp = b.membership?.canUpload ? 1 : 0;
          if (aUp !== bUp) return bUp - aUp;
          return 0;
        });
        if (sorted[0]?.event?.id) setEventId(sorted[0].event.id);
      } catch { /* ignore */ }
    })();
  }, [ready, eventId]);

  useEffect(() => {
    if (!ready || !eventId) return;
    (async () => {
      setLoading(true);
      try { const body = await listMySubmissions(eventId); setData(body); }
      catch { /* ignore */ }
      finally { setLoading(false); }
    })();
  }, [ready, eventId]);

  useEffect(() => {
    if (!data?.submissions) return;
    let cancelled = false;
    (async () => {
      for (const sub of data.submissions) {
        if (thumbs[sub.id] !== undefined || sub.status !== 'DELIVERED') continue;
        const url = await fetchSubmissionThumbnailDataUrl(sub.id);
        if (cancelled) return;
        setThumbs(prev => ({ ...prev, [sub.id]: url }));
      }
    })();
    return () => { cancelled = true; };
  }, [data]); // eslint-disable-line

  async function onWithdraw(subId) {
    if (!window.confirm('Withdraw this submission? The host will no longer see it.')) return;
    try { await withdrawSubmission(subId); const body = await listMySubmissions(eventId); setData(body); } catch { /* ignore */ }
  }

  const counts = data?.counts || {};

  return (
    <div className="max-w-4xl mx-auto px-4 py-6" data-testid="my-views-screen">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-[#146BFF] font-semibold text-sm mb-4 hover:underline" data-testid="my-views-back-btn">
        <ArrowLeft className="w-4 h-4" /> back
      </button>
      <h1 className="text-2xl font-extrabold text-[#0B1B46] mb-2">My Views</h1>

      <div className="bg-[#EFF5FF] border border-[#D6E4FF] rounded-xl p-3 flex items-start gap-2 mb-4">
        <Lock className="w-4 h-4 text-[#146BFF] mt-0.5 flex-shrink-0" />
        <p className="text-sm text-[#5B6472]">Only you can see your uploads. Other attendees can&apos;t view these.</p>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="bg-white border border-[#E3E8EF] rounded-xl p-3 text-center">
          <Image className="w-5 h-5 text-[#146BFF] mx-auto mb-1" />
          <p className="text-xl font-extrabold text-[#0B1B46]">{counts.photos || 0}</p>
          <p className="text-[11px] text-[#5B6472]">Photos</p>
        </div>
        <div className="bg-white border border-[#E3E8EF] rounded-xl p-3 text-center">
          <Film className="w-5 h-5 text-[#146BFF] mx-auto mb-1" />
          <p className="text-xl font-extrabold text-[#0B1B46]">{counts.videos || 0}</p>
          <p className="text-[11px] text-[#5B6472]">Videos</p>
        </div>
        <div className="bg-white border border-[#E3E8EF] rounded-xl p-3 text-center">
          <Upload className="w-5 h-5 text-[#146BFF] mx-auto mb-1" />
          <p className="text-xl font-extrabold text-[#0B1B46]">{counts.delivered || 0}</p>
          <p className="text-[11px] text-[#5B6472]">Delivered</p>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-8"><div className="w-8 h-8 border-2 border-[#146BFF] border-t-transparent rounded-full animate-spin" /></div>
      ) : !data?.submissions?.length ? (
        <div className="bg-white border border-[#E3E8EF] rounded-xl p-6 text-center">
          <p className="text-sm text-[#5B6472]">No submissions yet. Start capturing moments!</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {data.submissions.map(sub => (
            <div key={sub.id} className="relative bg-white border border-[#E3E8EF] rounded-xl overflow-hidden aspect-square group" data-testid={`my-view-${sub.id}`}>
              {thumbs[sub.id] ? (
                <img src={thumbs[sub.id]} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-[#F3F5F8] flex items-center justify-center">
                  {sub.media_kind === 'video' ? <Film className="w-8 h-8 text-[#5B6472]" /> : <Image className="w-8 h-8 text-[#5B6472]" />}
                </div>
              )}
              {sub.status === 'DELIVERED' && (
                <div className="absolute top-1.5 right-1.5">
                  <CheckCircle className="w-5 h-5 text-[#1F8A55] drop-shadow" />
                </div>
              )}
              {sub.status === 'DELIVERED' && (
                <button onClick={() => onWithdraw(sub.id)}
                  className="absolute bottom-1.5 right-1.5 opacity-0 group-hover:opacity-100 px-2 py-1 bg-black/60 text-white text-[10px] font-bold rounded transition-opacity">
                  Withdraw
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      <button onClick={() => navigate(`/contrib-upload?event_id=${eventId}`)}
        className="fixed bottom-20 md:bottom-8 right-6 w-14 h-14 bg-[#146BFF] hover:bg-[#0F55CC] text-white rounded-full flex items-center justify-center shadow-lg transition-colors z-40"
        data-testid="my-views-fab">
        <Plus className="w-6 h-6" />
      </button>
    </div>
  );
}
